"use client";

import React, { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import { format, startOfDay } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

interface DateRangeSelectorProps {
  onChange?: (startDate: Date | null, endDate: Date | null) => void;
  initialStartDate?: string; // YYYY-MM-DD string (preferred)
  initialEndDate?: string;
}

export default function DateRangeSelector({
  onChange,
  initialStartDate,
  initialEndDate,
}: DateRangeSelectorProps) {
  // Convert initial string dates to normalized Date objects (start of day)
  const getInitialDate = (dateStr?: string, fallback?: Date) => {
    if (!dateStr) return fallback;
    const date = new Date(dateStr);
    return startOfDay(date); // Ensures 00:00:00 local time
  };

  const today = startOfDay(new Date());

  const defaultEnd = startOfDay(new Date());
  defaultEnd.setDate(today.getDate() + 7);

  const [state, setState] = useState([
    {
      startDate: getInitialDate(initialStartDate, today),
      endDate: getInitialDate(initialEndDate, defaultEnd),
      key: "selection",
    },
  ]);

  const [isOpen, setIsOpen] = useState(false);
  const [focusedInput, setFocusedInput] = useState<"start" | "end">("start");

  const triggerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const start = state[0].startDate;
  const end = state[0].endDate;

  const handleSelect = (item) => {
    const newRange = item.selection;

    // Normalize both dates to start of day (prevents timezone shift)
    const normalizedStart = startOfDay(newRange.startDate);
    const normalizedEnd = startOfDay(newRange.endDate);

    const updatedRange = {
      ...newRange,
      startDate: normalizedStart,
      endDate: normalizedEnd,
    };

    setState([updatedRange]);

    if (onChange) {
      onChange(normalizedStart, normalizedEnd);
    }

    // Auto close logic
    if (focusedInput === "start") {
      setFocusedInput("end");
    } else {
      setIsOpen(false);
      setFocusedInput("start");
    }
  };

  const openWithFocus = (input: "start" | "end") => {
    setFocusedInput(input);
    setIsOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="w-full max-w-2xl mx-auto relative">
      <div className="mb-2 text-sm font-medium text-gray-700">Dates</div>

      <div
        ref={triggerRef}
        className="flex items-center bg-white border border-[#DDD] rounded-md px-3 py-2 cursor-pointer"
      >
        <div className="flex items-center flex-1 gap-3">
          <div
            onClick={() => openWithFocus("start")}
            className={`flex-1 rounded-xl cursor-pointer transition-all ${
              focusedInput === "start" ? "border-b-2 border-[#2D65F2]" : ""
            }`}
          >
            <div className="text-xs text-[#303030]">From</div>
            <div className="font-medium text-lg text-[#303030]">
              {format(start, "MMM d, yyyy")}
            </div>
          </div>

          <div className="text-3xl text-[#303030]">→</div>

          <div
            onClick={() => openWithFocus("end")}
            className={`flex-1 px-4 py-2.5 rounded-xl cursor-pointer transition-all ${
              focusedInput === "end" ? "border-b-2 border-[#2D65F2]" : ""
            }`}
          >
            <div className="text-xs text-[#303030]">To</div>
            <div className="font-medium text-lg text-[#303030]">
              {format(end, "MMM d, yyyy")}
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div ref={calendarRef} className="absolute top-32 z-50">
            <div className="bg-white rounded-md shadow-2xl overflow-hidden w-[560px]">
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-1 right-3 text-2xl text-gray-400 hover:text-gray-600 z-10"
              >
                ✕
              </button>

              <div className="p-5">
                <DateRange
                  editableDateInputs={false}
                  onChange={handleSelect}
                  moveRangeOnFirstSelection={false}
                  ranges={state}
                  months={2}
                  direction="horizontal"
                  minDate={new Date()}
                  rangeColors={["#2D65F2"]}
                  showDateDisplay={false}
                  monthWidth={205}
                  calendarWidth={430}
                  focusedRange={[0, focusedInput === "start" ? 0 : 1]}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
